import { Document, Packer, Paragraph, TextRun } from 'docx';
import PizZip from 'pizzip';

/**
 * The `docx` packager always emits `<w:docGrid w:linePitch="360"/>` in `sectPr`.
 * That OOXML element defines a vertical document grid; Word can surface it as
 * visible layout/grid lines. It is not from the RTF source — strip it for plain-text exports.
 *
 * @param {Blob} blob
 * @returns {Promise<Blob>}
 */
async function stripDocumentGridFromDocxBlob(blob) {
  const buf = await blob.arrayBuffer();
  const zip = new PizZip(buf);
  const path = 'word/document.xml';
  const entry = zip.file(path);
  if (!entry) return blob;

  let xml = entry.asText();
  xml = xml.replace(/<w:docGrid\b[^>]*\/\s*>/g, '');
  zip.file(path, xml);

  return zip.generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
  });
}

/**
 * Build a simple Word document (DOCX) from plain text (one paragraph per line).
 */
export async function plainTextToDocxBlob(plainText) {
  const lines = String(plainText ?? '').split(/\r?\n/);
  const children =
    lines.length > 0
      ? lines.map((line) => new Paragraph({ children: [new TextRun(line.length ? line : ' ')] }))
      : [new Paragraph({ children: [new TextRun(' ')] })];

  const doc = new Document({
    sections: [
      {
        children,
      },
    ],
  });

  const rawBlob = await Packer.toBlob(doc);
  return stripDocumentGridFromDocxBlob(rawBlob);
}
